import { oneLine, oneLineAddress } from './oneLine.js';
import { readAction, type LineAction } from './supportMail.js';

// The spam list (docs/modules/support-mail.md, "The spam list, on the screens"). Every field is read defensively:
// a server `null` where a list or an object was expected is common, and a Lit render that throws leaves a blank screen.

export const SPAM_SENTENCE = 'Emails Gmail filed as spam in the last 30 days. Gmail deletes them after that. Press Not spam to have COS read one like any other email.';
export const SPAM_NOT_READING_WORDS = 'COS is not reading the support mailbox, so nothing is listed here.';
export const SPAM_MOVED_WORDS = 'Moved to the inbox. COS will read it within a few minutes.';
export const SPAM_NOT_SENT_WORDS = 'The server did not send a list.';

export interface SpamItem {
	id: number;
	fromEmail: string;
	fromName: string;
	subject: string;
	receivedAt: string;
	canReadText: boolean;
	// false only when the server says so: a line it sends no verdict on is not accused of anything.
	senderVerified: boolean | null;
	wouldMatchTicketRef: string;
	wouldMatchTicketId: number;
	lookedAt: boolean;
	notSpam: LineAction;
	dismiss: LineAction;
}

export interface SpamRead {
	items: SpamItem[];
	skipped: number;
	received: number;
	total: number | null;
	firstPosition: number | null;
	orderWords: string;
	// false: COS is not reading the support mailbox (mode off); no list, no buttons.
	reading: boolean;
	unlooked: number;
	windowDays: number;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const count = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null);

export function readSpamCounted(v: unknown): SpamRead | null {
	if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
	const top = v as Record<string, unknown>;
	if (!Array.isArray(top.data)) return null;
	const items: SpamItem[] = [];
	let skipped = 0;
	for (const r of top.data) {
		const o = r && typeof r === 'object' ? (r as Record<string, unknown>) : null;
		const id = o ? Number(o.id) : NaN;
		if (!o || !Number.isFinite(id) || id <= 0) { skipped++; continue; }
		const ticketId = Number(o.would_match_ticket_id);
		items.push({
			id,
			fromEmail: str(o.from_email),
			fromName: str(o.from_name),
			subject: str(o.subject),
			receivedAt: str(o.received_at),
			canReadText: o.can_read_text === true,
			senderVerified: typeof o.sender_verified === 'boolean' ? o.sender_verified : null,
			wouldMatchTicketRef: oneLine(str(o.would_match_ticket_ref)),
			wouldMatchTicketId: Number.isFinite(ticketId) && ticketId > 0 ? Math.floor(ticketId) : 0,
			lookedAt: o.looked_at === true,
			// A line with no verdict is not offered: nothing here guesses that an action is allowed.
			notSpam: readAction(o.not_spam, false),
			dismiss: readAction(o.dismiss, false),
		});
	}
	const fp = count(top.first_position);
	const days = count(top.window_days);
	return {
		items,
		skipped,
		received: top.data.length,
		total: count(top.total),
		firstPosition: fp,
		orderWords: top.order === 'oldest_first' ? 'Oldest first' : top.order === 'newest_first' ? 'Newest first' : '',
		reading: top.reading !== false,
		unlooked: count(top.unlooked) ?? 0,
		windowDays: days && days > 0 ? days : 30,
	};
}

export function unlookedWords(n: number): string {
	return `${n.toLocaleString()} not looked at`;
}

// The sender, one line, with nothing of the markup a stranger put in a name or a subject.
export function spamFromLine(it: Pick<SpamItem, 'fromEmail' | 'fromName'>): string {
	const name = oneLine(it.fromName);
	const email = oneLineAddress(it.fromEmail);
	return name && email ? `${name} <${email}>` : email || name || 'Unknown sender';
}

// The bell (`support_mail_spam_waiting`): the screens build the sentence from the payload's `unlooked`.
export function spamBellWords(unlooked: unknown): string {
	const n = typeof unlooked === 'number' && Number.isFinite(unlooked) ? Math.floor(unlooked) : 0;
	if (n === 1) return '1 email in the support mailbox was filed as spam. Check it for a real reply.';
	if (n > 1) return `${n.toLocaleString()} emails in the support mailbox were filed as spam. Check them for real replies.`;
	return 'Emails in the support mailbox were filed as spam. Check them for real replies.';
}
